import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CardsShowcase.css";
import SideBar from "./SideBar";
import { RARITIES, resolveCardImage } from "./SitePetConfig";
import { fetchAllPetCards, getStoredPetCard } from "./SitePetProgress";
import { getPetPowerups, normalizePetStats } from "./PetPowerups";

function CardsShowcase() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rarityFilter, setRarityFilter] = useState("all");
  const [hasMyCard] = useState(() => Boolean(getStoredPetCard()));

  useEffect(() => {
    let alive = true;

    fetchAllPetCards().then((items) => {
      if (!alive) return;
      setCards(items);
      setLoading(false);
    });

    return () => {
      alive = false;
    };
  }, []);

  const filteredCards = useMemo(() => {
    if (rarityFilter === "all") return cards;
    return cards.filter((card) => card.rarity === rarityFilter);
  }, [cards, rarityFilter]);

  return (
    <div className="cards-showcase-page">
      <SideBar />

      <main className="cards-showcase">
        {!hasMyCard && (
          <section className="cards-showcase__cta">
            <div>
              <p>Your card</p>
              <h2>You have not created your character card yet.</h2>
              <span>
                You can create it anytime from here and it will still join the
                showcase flow.
              </span>
            </div>

            <button
              className="cards-showcase__ctaButton"
              onClick={() => navigate("/character-selection")}
            >
              Create My Card
            </button>
          </section>
        )}

        <header className="cards-showcase__header">
          <div>
            <p>Community pulls</p>
            <h1 style={{ fontSize: "2rem" }}>Cards Showcase</h1>
          </div>

          <div className="cards-showcase__filters" aria-label="Card rarity filters">
            <button
              className={rarityFilter === "all" ? "is-active" : ""}
              onClick={() => setRarityFilter("all")}
            >
              All
            </button>

            {Object.entries(RARITIES).map(([key, rarity]) => (
              <button
                key={key}
                className={rarityFilter === key ? "is-active" : ""}
                onClick={() => setRarityFilter(key)}
                style={{ "--filter-color": rarity.color }}
              >
                {rarity.label}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="cards-showcase__empty">Loading cards...</div>
        ) : filteredCards.length === 0 ? (
          <div className="cards-showcase__empty">No cards pulled yet.</div>
        ) : (
          <section className="cards-showcase__grid">
            {filteredCards.map((card) => {
              const rarity = RARITIES[card.rarity] || RARITIES.emerald;
              const image = resolveCardImage(card.card_image_path);
              const powerups = getPetPowerups(normalizePetStats(card.live_stats));

              return (
                <article
                  key={card.card_id || card.visitor_id}
                  className="showcase-card"
                  style={{
                    "--rarity-color": rarity.color,
                    "--rarity-gradient": rarity.gradient,
                  }}
                >
                  {image && (
                    <img
                      className="showcase-card__bg"
                      src={image}
                      alt=""
                      draggable="false"
                    />
                  )}

                  <div className="showcase-card__content">
                    <div className="showcase-card__top">
                      <span className="char-rarity">
                        {card.rarity_label || rarity.label}
                      </span>

                      <span className="char-power">{card.power || 0} HP</span>
                    </div>

                    <div className="showcase-card__art" />

                    <h2 className="char-name">{card.character_name}</h2>
                    <p>{card.flavor_line || "No card sentence saved."}</p>

                    <div className="showcase-card__powerups">
                      {powerups.slice(0, 4).map((item) => (
                        <span key={item.label}>
                          <strong>{item.value}</strong>
                          {item.label}
                        </span>
                      ))}
                    </div>

                    <div className="showcase-card__meta">
                      <span className="card-code">{card.card_code}</span>
                      <span className="card-sign">{card.signature || "unsigned"}</span>
                    </div>
                    <h5 className="card-code2">
                      © Ragav.me/Portfolio/CharacterCards/2026
                    </h5>
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
}

export default CardsShowcase;
